import React from 'react';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet';
import { mintNumberToName } from 'utils/token';
import { Box, Flex } from '../ui';
import PrintButtons from '../PrintButtons/PrintButtons';
import { useActiveBeat, useOriginalById } from 'hooks';
import ShareButtons from 'components/ShareButtons';
import { Original, DROP } from '../common';
import { getDropFromContractAddress } from 'utils/dropsHelpers';
import OwnershipDetails from './OwnershipDetails';
import { getRarity } from 'utils/metadata';
import { buildSharingMessageSubject, buildSharingMessageBody } from 'utils/helpers';
import BurnSign from 'components/ui/BurnSign';
import { useWalletItemsContext } from 'components/WalletItemsContext';
import get from 'lodash/get';

const BondingCurveLink = styled.a`
  color: white !important;
  text-decoration: underline;
`;

interface ActiveBeatProps {}
interface BeatDataProps {
  activeBeat: Original;
  isPrint?: boolean;
}

const BeatData: React.FC<BeatDataProps> = ({ activeBeat, isPrint = false }) => {
  const { print } = useOriginalById(activeBeat.contractAddress, activeBeat.id);
  const { getItem } = useWalletItemsContext();

  const drop = getDropFromContractAddress(activeBeat.contractAddress);
  const rarity = activeBeat && getRarity(activeBeat);
  const title = `${mintNumberToName(activeBeat.mintNumber, drop)}${isPrint ? ' - PRINT' : ''}`;

  const ownedOriginal = getItem(drop, activeBeat.id);
  const ownedPrint = getItem(drop, get(activeBeat, 'print.id'));

  const printsBalance = ownedPrint?.quantity || 0;
  const userOwnsPrints = Boolean(printsBalance);
  const userOwnsOriginal = Boolean(ownedOriginal);

  const isFutura = drop === DROP.futura;

  const printsData = print || activeBeat.print;

  return (
    <Box maxWidth="400px" margin="0 auto">
      <Helmet defer={false} title={`EulerBeats | ${title}`} />
      <Flex justifyBetween alignCenter p="1" width="100%" wrap="true" px={3}>
        {!isFutura && (
          <Box
            color="highlight"
            fontSize="3"
            fontWeight="700"
            width={[null, null, `${Boolean(rarity) ? '50%' : '100%'}`]}
            my={1}
          >
            {title}
          </Box>
        )}
        {rarity && (
          <Box fontSize={['1', '1', '2']} color="highlight" ml={['2', '3']}>
            {rarity}
          </Box>
        )}

        {!isFutura && (
          <Flex width={['100%', null]} minHeight="30px" justifyBetween alignEnd>
            {print && (
              <OwnershipDetails
                userOwnsPrints={userOwnsPrints}
                userOwnsOriginal={userOwnsOriginal}
                printsBalance={printsBalance}
                print={print}
                drop={drop}
              />
            )}
            {print && !userOwnsPrints && <BurnSign burnPrice={print.burnPrice} />}
          </Flex>
        )}
      </Flex>

      {!isFutura && (
        <>
          <Flex px={3} py={2}>
            {printsData && (
              <PrintButtons
                original={activeBeat}
                userOwnsPrints={userOwnsPrints}
                printsData={printsData}
              />
            )}
          </Flex>

          <Box textAlign="center" fontSize="12px" fontWeight="200" my={1}>
            Print & Burn prices vary with supply.&nbsp;
            <BondingCurveLink href={`${window.location.origin}/about#bonding-curves`}>
              See bonding curve details.
            </BondingCurveLink>
          </Box>
        </>
      )}

      <Box display={['none', 'block']} width="248px" margin="16px auto">
        <ShareButtons
          url={window.location.href}
          title={buildSharingMessageSubject(false)}
          text={buildSharingMessageBody(false, window.location.href)}
        />
      </Box>
    </Box>
  );
};

const ActiveBeat: React.FC<ActiveBeatProps> = () => {
  const { activeBeat, isPrint } = useActiveBeat();

  // TODO: empty state
  return (
    <Box mb="2" width="100%" maxWidth="500px">
      {activeBeat && <BeatData activeBeat={activeBeat} isPrint={isPrint} />}
    </Box>
  );
};

export default ActiveBeat;
